import Service, { service } from '@ember/service'
import { Product } from 'teacher-ui-client/utils/products'
import config from 'teacher-ui-client/config/environment'
import type AuthToken from '@blakeelearning/auth/services/auth-token'
import type { Log } from '@blakeelearning/log'
import { join, joinQueryParams, type QueryParams } from 'teacher-ui-client/utils/uri'
import { startOfDay, endOfDay, type NormalizedInterval, format } from 'date-fns'
import type { UIScope } from 'teacher-ui-client/reporting/ui-scope/route'
import downloadjs from 'downloadjs'
import type RouterService from '@ember/routing/router-service'
import type ImpressionService from 'teacher-ui-client/services/impression'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import type { IntlService } from 'ember-intl'

export class FetchReportError extends Error {
  response!: Response
  constructor(message: string, response: Response) {
    super(message)
    this.response = response
  }
}

const RequestScopesNames = {
  School: 'school',
  Teacher: 'teacher',
  SchoolClass: 'school-class',
  Student: 'student',
} as const
export type RequestScopeName = (typeof RequestScopesNames)[keyof typeof RequestScopesNames]

/**
 * Represents the scope in which we request data. This differs to UIScope, allowing a scope of student.
 */
export class RequestScope {
  id: string

  scope: RequestScopeName

  constructor(scope: RequestScopeName, id: string) {
    this.id = id
    this.scope = scope
  }

  get path() {
    return `${this.scope}/${this.id}`
  }
}

/**
 * A UIScope can be directly converted to a RequestScope
 */
export function toRequestScope(uiScope: UIScope) {
  return new RequestScope(uiScope.scope, uiScope.id)
}

/**
 * Creates a RequestScope from a student id
 */
export function toRequestScopeStudent(studentId: string) {
  return new RequestScope(RequestScopesNames.Student, studentId)
}

/**
 * To help generate standard filenames for exports - file extension to be added based on the format you want.
 */
export function exportFilename(product: Product, userFriendlyReportName: string, ext: string) {
  const date = format(new Date(), 'yyyy-MM-dd_HH.mm.ss')
  return `${product}_${userFriendlyReportName}_${date}${ext}`
}

interface ReportingBuildQueryArgs {
  sort?: ReportingQuerySort
  page?: ReportingQueryPage
  filter?: ReportingQueryFilter
  period?: NormalizedInterval
  contentLevel?: string
}

export type ReportingQuerySort = string

export type ReportingQueryPage =
  | {
      number: number
      size: number
    }
  | 'all'

export type ReportingQueryFilter = QueryParams

interface ReportingQuery extends QueryParams {
  sort?: ReportingQuerySort
  page?: ReportingQueryPage
  filter?: ReportingQueryFilter
}

export type BaseReport = object

export interface BaseReportMetaPaging {
  page_number: number
  page_size: number
  total_entries: number
  total_pages: number
}

export interface BaseReportMetaStandardsSet {
  standards_set_id: Nullable<string>
  standards_set_name: Nullable<string>
}

export default class ReportingService extends Service {
  @service impression!: ImpressionService

  @service router!: RouterService

  @service authToken!: AuthToken

  @service log!: Log

  @service intl!: IntlService

  @service flashQueue!: FlashQueueService

  buildGravityUrl({
    requestScope,
    product,
    reportName,
  }: {
    requestScope: RequestScope
    product: Product
    reportName: string
  }): string {
    const productPath = String(product)

    const joined = join(config.gravityV3Url, 'teacher-ui/reporting', requestScope.path, productPath, reportName)
    const url = joined.href

    return url
  }

  buildWritingLegendsUrl({
    requestScope,
    product,
    reportName,
  }: {
    requestScope: RequestScope
    product: Product
    reportName: string
  }): string {
    const productPath = String(product)

    const joined = join(config.writingLegendsApiUrl, 'reporting', requestScope.path, productPath, reportName)
    const url = joined.href

    return url
  }

  /* Dates provided here for the period are only meant to be taken for their year/month/day values. The user only
   * selects _dates_ from the date picker (not times), and as such they should be inclusive for both start & end.
   * This should calculation should be based on the timezone the user is currently in (i.e. what the date already
   * has applied to it), _and then_ converted to an ISO string as the last step before handing to the backend to use
   * for searching events.
   *
   * For example;
   *  period.end = new Date(2014, 9, 10)  => Fri Oct 10 2014 00:00:00 GMT+1000
   *  endOfDay(period.end)                => Fri Oct 10 2014 23:59:59 GMT+1000
   *  endOfDay(period.end).toISOString()  => 2014-10-10T13:59:59.999Z
   */
  buildQueryPeriod({ start, end }: NormalizedInterval) {
    return {
      start: startOfDay(new Date(start)).toISOString(),
      end: endOfDay(new Date(end)).toISOString(),
    }
  }

  /**
   * Every arg is optional - if provided, it'll be set in the appropriate place in the query.
   */
  buildQuery(queryArgs: ReportingBuildQueryArgs): ReportingQuery {
    const { sort, page, filter, period, contentLevel } = queryArgs

    const query: ReportingQuery = {}
    if (sort) query.sort = sort
    if (page) query.page = page
    if (filter) query.filter = filter
    if (period) {
      query.filter = { ...(query.filter ?? {}) }
      query.filter.period = this.buildQueryPeriod(period)
    }
    if (contentLevel && contentLevel !== 'all') {
      query.filter = { ...(query.filter ?? {}), 'content-levels': [contentLevel] }
    }

    return query
  }

  get headers() {
    return {
      Authorization: this.authToken.token ?? '',
      Accept: 'application/json',
      'Content-Type': 'application/json',
    }
  }

  /**
   * Fetches a report with the given url and query. Will log & throw an error on failure - your caller should handle
   * this in some way, to display an error UI.
   */
  async fetchReport<R extends BaseReport>(url: string, query: ReportingQuery): Promise<R> {
    try {
      const response = await fetch(joinQueryParams(url, query), {
        method: 'GET',
        headers: this.headers,
      })

      if (response.ok) {
        return (await response.json()) as R
      } else {
        throw new FetchReportError('response not ok', response)
      }
    } catch (e: unknown) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        // Fetch aborted by user action, eg closed tab, stop button, network outage. No need to log.
        throw e
      } else if (e instanceof FetchReportError) {
        this.log.error(`Failed to fetch report; Reason: (${e.response.status})`, e.response, url, query)
        throw e
      }
      this.log.error(`Failed to fetch report; Reason: (unknown error thrown)`, e ?? '', url, query)
      throw e
    }
  }

  // settings as property on service allows for stubbing in tests
  downloadjs = downloadjs

  private defaultExportErrorHandler = (_e: unknown) => {
    this.flashQueue.addFail({ subtitle: this.intl.t('failedToExportReport') })
  }

  /**
   * Similar to the fetchReport function, but with Accept CSV header, and downloads the result.
   *
   * Does not throw, rather provides an option for handling errors with a default flash message. A failed export action
   * typically does not require UI page changes, and can be handled with a simple flash.
   */
  async exportReportCsv(
    url: string,
    query: ReportingQuery,
    filename = 'export.csv',
    errorHandler = this.defaultExportErrorHandler,
  ): Promise<void> {
    try {
      const response = await fetch(joinQueryParams(url, query), {
        method: 'GET',
        headers: {
          ...this.headers,
          Accept: 'text/csv',
        },
      })

      if (response.ok) {
        const blob = await response.blob()
        this.downloadjs(blob, filename, 'text/csv')
        return
      } else {
        throw new FetchReportError('response not ok', response)
      }
    } catch (e: unknown) {
      if (e instanceof DOMException && e.name === 'AbortError') {
        // Fetch aborted by user action, eg closed tab, stop button, network outage. Do nothing.
      } else if (e instanceof FetchReportError) {
        this.log.error(`Failed to export report; Reason: (${e.response.status})`, e.response, url, query)
      } else {
        this.log.error(`Failed to export report; Reason: (unknown error thrown)`, e ?? '', url, query)
      }
      errorHandler(e)
    }
  }
  /**
   * Makes an impression for the current report path
   */
  makeImpression = () => {
    if (this.router.currentRouteName) this.impression.make(this.router.currentRouteName)
  }

  /**
   * Obtain the product for the given reporting route name.
   */
  get product(): Product | undefined {
    const routeName = this.router.currentRouteName
    if (routeName?.includes('reporting.ui-scope.re.')) return Product.RE
    if (routeName?.includes('reporting.ui-scope.rex.')) return Product.REX
    if (routeName?.includes('reporting.ui-scope.fast-phonics.')) return Product.FP
    if (routeName?.includes('reporting.ui-scope.maths.')) return Product.MS
    if (routeName?.includes('reporting.ui-scope.mathseeds-prime.')) return Product.MSP
    if (routeName?.includes('reporting.ui-scope.writing-legends.')) return Product.WL
  }
}
declare module '@ember/service' {
  interface Registry {
    reporting: ReportingService
  }
}
